import React, { useEffect, useState } from 'react';
import { minutesLedgerAPI } from '../../services/api';

const MEETING_TYPE_LABEL = {
  board: 'Board Meetings',
  agm: 'AGMs',
  egm: 'EGMs',
  committee: 'Committee Meetings',
};

// State → palette. `accent` colours the icon chip + page pill; `bg`/`border` wash
// the card. Three tones only: teal (normal), amber (problem), neutral (idle).
const TONES = {
  teal: {
    border: '#cfe3e3', bg: '#f1f7f7', heading: '#0f4c4c', sub: '#4d7a78',
    icon: '#1e8a8a', chip: '#dcecec', pillBg: '#145f5f', pillText: '#ffffff',
  },
  amber: {
    border: '#f0d9a0', bg: '#fff7e6', heading: '#8a6a14', sub: '#9c7e2e',
    icon: '#c39a2e', chip: '#f7e8c2', pillBg: '#b8973a', pillText: '#ffffff',
  },
  neutral: {
    border: '#e6e1d4', bg: '#faf8f4', heading: '#5a5246', sub: '#928873',
    icon: '#a89e88', chip: '#efeadd', pillBg: '#8a8170', pillText: '#ffffff',
  },
};

const iconBase = {
  fill: 'none', stroke: 'currentColor', strokeWidth: 1.7,
  strokeLinecap: 'round', strokeLinejoin: 'round',
};

const PagesIcon = (p) => (
  <svg viewBox="0 0 24 24" width="14" height="14" {...iconBase} {...p}>
    <path d="M8 3h6l4 4v11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" />
    <path d="M14 3v4h4" />
    <path d="M9.5 13h5M9.5 16h3.5" />
  </svg>
);

const AlertIcon = (p) => (
  <svg viewBox="0 0 24 24" width="14" height="14" {...iconBase} {...p}>
    <path d="M12 4 2.8 20h18.4L12 4Z" />
    <path d="M12 10v4.5" />
    <path d="M12 17.6h.01" />
  </svg>
);

const InfoIcon = (p) => (
  <svg viewBox="0 0 24 24" width="14" height="14" {...iconBase} {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </svg>
);

/**
 * Shows what page the next set of minutes will start on, continuing the financial-year
 * sequence for this (company, meeting type, FY). Self-fetching; non-blocking on error.
 * Hands the resolved ledger view upward via onLedger so the page can decide whether
 * the manual start-page override is even applicable (it isn't, once history exists —
 * the backend ignores the override to avoid mid-sequence gaps).
 */
export default function PageContinuityBanner({
  companyCin,
  meetingType,
  meetingDate,
  committeeKey,
  refreshTick = 0,
  onLedger,
}) {
  const [state, setState] = useState({ status: 'idle' });

  useEffect(() => {
    let cancelled = false;
    if (!companyCin || !meetingType) {
      setState({ status: 'idle' });
      if (onLedger) onLedger({ suggestedStartPage: 1, hasHistory: false, eligible: false });
      return undefined;
    }
    setState({ status: 'loading' });
    minutesLedgerAPI
      .getLedger({ companyCin, meetingType, committeeKey, meetingDate })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || res || {};
        setState({ status: 'ready', data });
        if (onLedger) {
          onLedger({
            suggestedStartPage: data.suggestedStartPage || 1,
            hasHistory: !!data.hasHistory,
            eligible: data.eligible !== false,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCin, meetingType, committeeKey, meetingDate, refreshTick]);

  if (state.status === 'idle') return null;

  const d = state.data || {};
  const typeLabel = MEETING_TYPE_LABEL[d.meetingType] || 'Meetings';

  // ── pick tone + content per state ─────────────────────────────────────────
  let tone = 'teal';
  let Icon = PagesIcon;
  let heading = null;
  let sub = null;
  let loading = false;

  if (state.status === 'loading') {
    tone = 'neutral';
    loading = true;
  } else if (state.status === 'error') {
    tone = 'amber';
    Icon = AlertIcon;
    heading = 'Continuous numbering unavailable';
    sub = 'This PDF will start at page 1 — set it manually below.';
  } else if (!d.eligible) {
    tone = 'neutral';
    Icon = InfoIcon;
    heading = 'No company selected';
    sub = 'Minutes will be numbered from page 1.';
  } else if (!d.hasHistory) {
    heading = <PageHeading tone="teal" page={d.suggestedStartPage} />;
    sub = (
      <>
        First minutes of <Em tone="teal">{d.financialYear}</Em> · {typeLabel}
      </>
    );
  } else {
    heading = <PageHeading tone="teal" page={d.suggestedStartPage} />;
    sub = (
      <>
        Continuing <Em tone="teal">{d.financialYear}</Em> · {typeLabel} · last ended p.{d.lastEndPage}
      </>
    );
  }

  const t = TONES[tone];

  return (
    <div
      style={{
        display: 'flex',
        gap: 9,
        alignItems: 'flex-start',
        padding: '9px 11px',
        borderRadius: 10,
        border: `1px solid ${t.border}`,
        background: t.bg,
        fontFamily: "'Outfit', sans-serif",
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 22,
          height: 22,
          marginTop: 1,
          borderRadius: 7,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: t.chip,
          color: t.icon,
        }}
      >
        <Icon />
      </span>

      <div style={{ minWidth: 0, flex: 1 }}>
        {loading ? (
          <div className="animate-pulse">
            <div style={{ width: '72%', height: 9, borderRadius: 5, background: '#e6e1d4' }} />
            <div style={{ width: '46%', height: 8, borderRadius: 5, background: '#eee9dd', marginTop: 6 }} />
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 6,
                fontSize: 12.5,
                fontWeight: 600,
                color: t.heading,
                lineHeight: 1.3,
              }}
            >
              {heading}
            </div>
            {sub && (
              <div style={{ fontSize: 11, color: t.sub, lineHeight: 1.45, marginTop: 2 }}>
                {sub}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// "Starts at page" + the page number as an emphasised pill. Rendered as flex
// children of the heading row so the label and pill share one centred axis
// (the old inline-flex-in-a-block made the pill ride the text baseline).
function PageHeading({ tone, page }) {
  const t = TONES[tone];
  return (
    <>
      <span style={{ lineHeight: 1.2 }}>Starts at page</span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 20,
          height: 18,
          padding: '0 7px',
          background: t.pillBg,
          color: t.pillText,
          fontWeight: 700,
          fontSize: 11.5,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          borderRadius: 6,
        }}
      >
        {page}
      </span>
    </>
  );
}

// Inline emphasis inside the muted sub-line (e.g. the FY label).
function Em({ tone, children }) {
  const t = TONES[tone];
  return <strong style={{ color: t.heading, fontWeight: 600 }}>{children}</strong>;
}
